/**
 * Facet configuration for Algolia searches.
 *
 * Facets in breitling indexes are locale-specific for translated attributes.
 * Format: attributeName.{LOCALE_CODE} (e.g., "dial_color.EN_US")
 */

/**
 * Standard facets for watch products.
 *
 * Some attributes are locale-independent (size, collection, price),
 * while others need locale codes (type, case, dial_color).
 *
 * Bracelet category color/material use singular pipe-format attrs
 * (`bracelet_category_color.{LOCALE}` / `bracelet_category_material.{LOCALE}`)
 * from BEC-24331 — same `{label} | {id}` pattern as dial_color/case.
 * Legacy plural attrs (`bracelet_category_colors` / `bracelet_category_materials`)
 * stay for Salesforce and must not be used for PLP faceting.
 */
export const WATCH_FACETS = {
  // Locale-independent facets
  size: 'size',
  collection: 'collection',
  price: 'price',

  // Locale-dependent facets (use template with {LOCALE})
  type: 'type.{LOCALE}',
  case: 'case.{LOCALE}',
  dialColor: 'dial_color.{LOCALE}',

  // Watch-level bracelet category facets (singular pipe-format, BEC-24331)
  braceletMaterial: 'bracelet_category_material.{LOCALE}',
  braceletColor: 'bracelet_category_color.{LOCALE}',
} as const;

/**
 * Standard facets for strap products.
 *
 * Actual Algolia attribute names (verified from index):
 * - lug_size (lug width - locale-independent numeric string like "24")
 * - lug_type.{locale} (straight/curved)
 * - material.{locale}
 * - color.{locale}
 * - buckle_type.{locale}
 *
 * **LIMITATION**: Strap facets in Algolia do NOT have language-agnostic IDs.
 * Values are stored as plain localized strings (e.g., "Straight" in EN, "Derecho" in ES).
 * This means filter selections will NOT persist when users switch languages.
 * See facetValues.ts for detailed documentation on ID availability.
 */
export const STRAP_FACETS = {
  // Locale-independent
  price: 'price',
  lug: 'lug_size', // Lug width (numeric string like "24")

  // Locale-dependent
  lugType: 'lug_type.{LOCALE}',
  material: 'material.{LOCALE}',
  color: 'color.{LOCALE}',
  buckle: 'buckle_type.{LOCALE}',
} as const;

/**
 * Eyewear facets.
 * These match the actual facets available in the Algolia index for eyewear products.
 *
 * **PARTIAL ID SUPPORT**:
 * - `lens_color`: HAS IDs (e.g., "Blue | V-LENS-COLOR-3001") - cross-language works ✓
 * - `frame_material`: NO IDs (plain strings) - won't persist across language switch
 *
 * See facetValues.ts for detailed documentation on ID availability.
 */
export const EYEWEAR_FACETS = {
  // Locale-independent
  price: 'price',

  // Locale-dependent
  frameMaterial: 'frame_material.{LOCALE}',
  lensColor: 'lens_color.{LOCALE}',
} as const;

/**
 * Get facet attribute names for watch products with locale substitution.
 *
 * @param localeCode - Locale code in Algolia format (e.g., 'EN_US')
 * @returns Array of facet attribute names
 */
export function getWatchFacets(localeCode: string): string[] {
  return [
    WATCH_FACETS.size,
    WATCH_FACETS.collection,
    WATCH_FACETS.price,
    WATCH_FACETS.type.replace('{LOCALE}', localeCode),
    WATCH_FACETS.case.replace('{LOCALE}', localeCode),
    WATCH_FACETS.dialColor.replace('{LOCALE}', localeCode),
    WATCH_FACETS.braceletMaterial.replace('{LOCALE}', localeCode),
    WATCH_FACETS.braceletColor.replace('{LOCALE}', localeCode),
  ];
}

/**
 * Get facet attribute names for strap products with locale substitution.
 *
 * @param localeCode - Locale code in Algolia format (e.g., 'EN_US')
 * @returns Array of facet attribute names
 */
export function getStrapFacets(localeCode: string): string[] {
  return [
    STRAP_FACETS.price,
    STRAP_FACETS.lug,
    STRAP_FACETS.lugType.replace('{LOCALE}', localeCode),
    STRAP_FACETS.material.replace('{LOCALE}', localeCode),
    STRAP_FACETS.color.replace('{LOCALE}', localeCode),
    STRAP_FACETS.buckle.replace('{LOCALE}', localeCode),
  ];
}

/**
 * Get facet attribute names for eyewear products with locale substitution.
 *
 * @param localeCode - Locale code in Algolia format (e.g., 'EN_US')
 * @returns Array of facet attribute names
 */
export function getEyewearFacets(localeCode: string): string[] {
  return [
    EYEWEAR_FACETS.price,
    EYEWEAR_FACETS.frameMaterial.replace('{LOCALE}', localeCode),
    EYEWEAR_FACETS.lensColor.replace('{LOCALE}', localeCode),
  ];
}

/**
 * Map URL parameter names to Algolia facet base names.
 * Used for parsing URL params into filter state.
 */
export const URL_PARAM_TO_FACET: Record<string, string> = {
  // Watch facets
  collection: 'collection',
  size: 'size',
  case: 'case',
  'dial-color': 'dial_color',
  type: 'type',
  'bracelet-material': 'bracelet_category_material',
  'bracelet-color': 'bracelet_category_color',
  // Strap facets
  material: 'material',
  color: 'color',
  lug: 'lug_size',
  'lug-type': 'lug_type',
  buckle: 'buckle_type',
  // Eyewear facets (actual facets available in the index)
  'frame-material': 'frame_material',
  'lens-color': 'lens_color',
};

/**
 * Map Algolia facet base names to URL parameter names.
 * Used for generating URLs from filter state.
 */
export const FACET_TO_URL_PARAM: Record<string, string> = {
  // Watch facets
  collection: 'collection',
  size: 'size',
  case: 'case',
  dial_color: 'dial-color',
  type: 'type',
  bracelet_category_material: 'bracelet-material',
  bracelet_category_color: 'bracelet-color',
  // Strap facets
  material: 'material',
  color: 'color',
  lug_size: 'lug',
  lug_type: 'lug-type',
  buckle_type: 'buckle',
  // Eyewear facets (actual facets available in the index)
  frame_material: 'frame-material',
  lens_color: 'lens-color',
};

/**
 * Get the display name for a facet (for UI).
 */
export const FACET_DISPLAY_NAMES: Record<string, string> = {
  // Watch facets
  collection: 'Collection',
  size: 'Size',
  case: 'Case',
  dial_color: 'Dial Color',
  type: 'Type',
  price: 'Price',
  bracelet_category_material: 'Strap Material',
  bracelet_category_color: 'Strap Color',
  // Strap facets
  material: 'Material',
  color: 'Color',
  lug_size: 'Lug Width',
  lug_type: 'Lug Type',
  buckle_type: 'Buckle',
  // Eyewear facets (actual facets available in the index)
  frame_material: 'Frame Material',
  lens_color: 'Lens Color',
};

/**
 * Extract the base facet name from a locale-specific facet.
 *
 * @example
 * getBaseFacetName('dial_color.EN_US') // 'dial_color'
 * getBaseFacetName('size') // 'size'
 * getBaseFacetName('bracelet_category_color.EN_GB') // 'bracelet_category_color'
 */
export function getBaseFacetName(facetName: string): string {
  const dotIndex = facetName.indexOf('.');
  return dotIndex > 0 ? facetName.substring(0, dotIndex) : facetName;
}
